import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { cn } from '@/lib/utils'
import { SortableLeadCard } from './LeadCard'
import type { LeadStage, LeadWithRelations } from '@limo/shared'

interface LeadKanbanColumnProps {
  stage: LeadStage
  leads: LeadWithRelations[]
  onLeadClick: (leadId: string) => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function LeadKanbanColumn({
  stage,
  leads,
  onLeadClick,
  isCollapsed = false,
  onToggleCollapse,
}: LeadKanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  })

  const leadIds = leads.map(l => l.id)

  // Collapsed view for won/lost stages
  if (isCollapsed) {
    return (
      <div className="flex-shrink-0 w-16">
        <button
          onClick={onToggleCollapse}
          className="w-full h-full min-h-[300px] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 hover:bg-muted/50 transition-colors"
          style={{ borderColor: `${stage.color}40` }}
        >
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: stage.color }}
          />
          <span
            className="text-xs font-medium writing-mode-vertical"
            style={{
              color: stage.color,
              writingMode: 'vertical-lr',
              textOrientation: 'mixed',
            }}
          >
            {stage.name}
          </span>
          <span className="text-xs text-muted-foreground">{leads.length}</span>
        </button>
      </div>
    )
  }

  return (
    <div className="flex-shrink-0 w-72">
      {/* Column Header */}
      <div
        className="flex items-center justify-between p-3 rounded-t-lg"
        style={{ backgroundColor: `${stage.color}15` }}
      >
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: stage.color }}
          />
          <h3 className="font-medium text-sm">{stage.name}</h3>
          <span
            className="text-xs px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: `${stage.color}20`,
              color: stage.color,
            }}
          >
            {leads.length}
          </span>
        </div>
        {(stage.isWon || stage.isLost) && onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Collapse
          </button>
        )}
      </div>

      {/* Column Body */}
      <div
        ref={setNodeRef}
        className={cn(
          'p-2 rounded-b-lg border-2 border-t-0 min-h-[400px] max-h-[600px] overflow-y-auto transition-colors',
          isOver ? 'bg-primary/5 border-primary/30' : 'bg-muted/30 border-transparent'
        )}
      >
        <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {leads.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No leads
              </div>
            ) : (
              leads.map((lead) => (
                <SortableLeadCard
                  key={lead.id}
                  lead={lead}
                  onClick={() => onLeadClick(lead.id)}
                />
              ))
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  )
}
